import React, { useCallback } from "react";
import { sendClientSideSplitTrackEvent } from "@leafly-com/split-next";

import { getSplitKey } from "utils/split/getSplitKey";

import FilterShortcut from "components/botanic/FilterShortcut";
import { MobileFilterShortcutsType } from "components/FilteredGrid/MobileFilters";
import { usePublishFilterClickEvent } from "components/MenuFilters/hooks";
import {
  MenuFilterGroupTypes,
  MenuFiltersGroup,
  MenuFiltersToggle,
} from "components/MenuFilters/types/MenuFilters";

import { useFilterQueryParams } from "../hooks";

const MobileMenuFilterShortcuts: MobileFilterShortcutsType = ({
  onShortcutClick,
  availableFilters,
  menuPage,
  shallowRouting = true,
  textColorClass,
}) => {
  const {
    actions: { toggleFilter },
  } = useFilterQueryParams(shallowRouting);
  const { publishFilterSectionClickEvent } =
    usePublishFilterClickEvent(menuPage);

  const splitKey = getSplitKey();

  const trackClickEvent = (value: string) => {
    sendClientSideSplitTrackEvent(
      {
        eventType: "SHH-869_quickFilter_click",
        properties: { value },
      },
      splitKey,
    );
  };

  const onFilterSectionClick = useCallback(
    ({ name, type }: MenuFiltersGroup | MenuFiltersToggle) => {
      publishFilterSectionClickEvent(name);

      trackClickEvent(name);

      if (type === MenuFilterGroupTypes.toggle) {
        return toggleFilter(name, "true");
      }

      onShortcutClick(name);
    },
    [onShortcutClick, publishFilterSectionClickEvent, toggleFilter],
  );

  return (
    <>
      {availableFilters
        ? availableFilters
            .filter((filter: MenuFiltersGroup | MenuFiltersToggle) =>
              filter.type === "toggle"
                ? (filter as MenuFiltersToggle).count > 0
                : (filter as MenuFiltersGroup).values.length > 0,
            )
            .map((filter: MenuFiltersGroup | MenuFiltersToggle) => (
              <MobileMenuFilterShortcut
                key={filter.name}
                text={filter.label as string}
                type={filter.type}
                onClick={() => onFilterSectionClick(filter)}
                textColorClass={textColorClass}
              />
            ))
        : null}
    </>
  );
};

const MobileMenuFilterShortcut: React.FC<{
  onClick: () => void;
  text: string;
  type: MenuFilterGroupTypes;
  textColorClass?: string;
}> = ({ onClick, text, type, textColorClass }) => {
  const isToggleFilter = type === MenuFilterGroupTypes.toggle;
  return (
    <FilterShortcut
      showChevron={!isToggleFilter}
      text={text}
      onClick={onClick}
      className={textColorClass}
    />
  );
};

export default MobileMenuFilterShortcuts;
